import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiShoppingCart, FiCreditCard, FiCheck, FiAlertCircle, FiLoader, FiTag, FiX } from 'react-icons/fi';
import { cartService, CartItem } from '../services/cartService';
import { apiUrls } from '../config/api';
import Header from '../components/Header';

interface CheckoutPageProps {
  onHomeClick: () => void;
  onBooksClick: () => void;
  onVideoLearningClick: () => void;
  onBlogsClick: () => void;
  onLoginClick: () => void;
  onAdminClick: () => void;
  onMkAcademyOnlineClick: () => void;
  onMkAcademyInPersonClick: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  user: any;
  onLogout: () => void;
}

interface CouponData {
  code: string;
  name: string;
  discount_type: string;
  discount_value: number;
  discount_amount: number;
}

const CheckoutPage: React.FC<CheckoutPageProps> = (props) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [subtotal, setSubtotal] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState<CouponData | null>(null);
  const [finalTotal, setFinalTotal] = useState(0);
  
  // Default to test payment for admins, paypal for regular users
  const [paymentMethod, setPaymentMethod] = useState<'paypal' | 'test'>(props.isAdmin ? 'test' : 'paypal');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);

  // Billing info
  const [billingInfo, setBillingInfo] = useState({
    name: props.user?.name || '',
    email: props.user?.email || '',
    phone: ''
  });

  useEffect(() => {
    loadCartData();
    loadCouponFromCart();
  }, []);

  const loadCartData = () => {
    const items = cartService.getCartItems();
    const total = cartService.getCartTotal();
    setCartItems(items);
    setSubtotal(total);
    setFinalTotal(total);
  };

  const loadCouponFromCart = () => {
    // Load coupon from session storage if it was applied in cart
    const savedCoupon = sessionStorage.getItem('appliedCoupon');
    if (savedCoupon) {
      const couponData = JSON.parse(savedCoupon);
      setAppliedCoupon(couponData);
      setFinalTotal(couponData.finalTotal || subtotal);
    }
  };

  const formatPrice = (price: number): string => {
    return price.toFixed(2);
  };

  const handlePayPalPayment = async () => {
    // PayPal integration would go here
    // For now, showing how to initiate PayPal checkout
    alert('PayPal integration: You would be redirected to PayPal for payment');
    await processPayment('paypal');
  };

  const handleTestPayment = async () => {
    await processPayment('test');
  };

  const processPayment = async (method: 'paypal' | 'test') => {
    if (!props.isAuthenticated) {
      setError('Please log in to complete checkout');
      return;
    }

    if (!billingInfo.name || !billingInfo.email) {
      setError('Please fill in all billing information');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Prepare order data
      const orderData = {
        user_id: props.user.id,
        items: cartItems.map(item => ({
          id: item.id,
          type: item.type,
          name: item.title,
          title: item.title,
          price: item.price,
          quantity: item.quantity,
          image: item.image,
          // Add subscription dates for packages
          subscription_start_date: item.type === 'package' ? new Date().toISOString().split('T')[0] : null,
          subscription_end_date: item.type === 'package' 
            ? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] 
            : null,
          subscription_duration_months: item.type === 'package' ? 12 : null
        })),
        subtotal,
        coupon_code: appliedCoupon?.code || null,
        coupon_id: null, // Would need to fetch this if you have it
        discount_amount: appliedCoupon?.discount_amount || 0,
        total_amount: finalTotal,
        billing_info: billingInfo,
        payment_method: method
      };

      // Create order
      const createResponse = await fetch(apiUrls.payments.createOrder, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData)
      });

      const createData = await createResponse.json();

      if (!createData.success) {
        throw new Error(createData.error || 'Failed to create order');
      }

      // If test payment, auto-capture
      if (method === 'test') {
        // Simulate test payment capture
        const captureResponse = await fetch(apiUrls.payments.testCapture, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            paymentId: createData.payment.id,
            order_number: createData.order.order_number,
            shouldFail: false
          })
        });

        const captureData = await captureResponse.json();

        if (captureData.success) {
          setSuccess(true);
          setOrderNumber(createData.order.order_number);
          cartService.clearCart();
          sessionStorage.removeItem('appliedCoupon');
        } else {
          throw new Error('Payment failed');
        }
      } else {
        // PayPal - redirect to approval URL
        if (createData.payment.approveUrl) {
          window.location.href = createData.payment.approveUrl;
        }
      }

    } catch (err: any) {
      console.error('Payment error:', err);
      setError(err.message || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (success) {
    return (
      <PageContainer>
        <Header {...props} />
        <ContentContainer>
          <SuccessCard>
            <SuccessIcon><FiCheck /></SuccessIcon>
            <SuccessTitle>Payment Successful!</SuccessTitle>
            <SuccessMessage>
              Thank you for your purchase. Your order has been confirmed.
            </SuccessMessage>
            {orderNumber && (
              <OrderNumber>
                Order Number: <strong>{orderNumber}</strong>
              </OrderNumber>
            )}
            <SuccessActions>
              <button onClick={props.onHomeClick}>
                Return to Home
              </button>
              <button onClick={() => window.location.href = '/?page=profile'}>
                View My Profile
              </button>
            </SuccessActions>
          </SuccessCard>
        </ContentContainer>
      </PageContainer>
    );
  }

  if (cartItems.length === 0) {
    return (
      <PageContainer>
        <Header {...props} />
        <ContentContainer>
          <EmptyCart>
            <FiShoppingCart size={64} />
            <h2>Your cart is empty</h2>
            <p>Add some items to your cart before checking out</p>
            <button onClick={props.onBooksClick}>
              Browse Books
            </button>
          </EmptyCart>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header {...props} />
      <ContentContainer>
        <CheckoutContainer>
          <CheckoutTitle>
            <FiShoppingCart /> Checkout
          </CheckoutTitle>

          <CheckoutGrid>
            {/* Left Column - Order Summary */}
            <OrderSummarySection>
              <SectionTitle>Order Summary</SectionTitle>
              
              <OrderItems>
                {cartItems.map((item) => (
                  <OrderItem key={`${item.id}-${item.type}`}>
                    <ItemImage src={item.image || '/mathknots-logo.png'} alt={item.title} />
                    <ItemDetails>
                      <ItemName>{item.title}</ItemName>
                      <ItemType>{item.type === 'book' ? '📚 Book' : '📦 Package'}</ItemType>
                      <ItemQuantity>Qty: {item.quantity}</ItemQuantity>
                    </ItemDetails>
                    <ItemPrice>${formatPrice(item.price * item.quantity)}</ItemPrice>
                  </OrderItem>
                ))}
              </OrderItems>

              <PriceSummary>
                <PriceRow>
                  <span>Subtotal:</span>
                  <span>${formatPrice(subtotal)}</span>
                </PriceRow>

                {appliedCoupon && (
                  <PriceRow discount>
                    <span>
                      <FiTag /> Discount ({appliedCoupon.code}):
                    </span>
                    <span>-${formatPrice(appliedCoupon.discount_amount)}</span>
                  </PriceRow>
                )}

                <TotalRow>
                  <span>Total:</span>
                  <span>${formatPrice(finalTotal)}</span>
                </TotalRow>

                {appliedCoupon && (
                  <SavingsText>
                    You save ${formatPrice(appliedCoupon.discount_amount)}!
                  </SavingsText>
                )}
              </PriceSummary>
            </OrderSummarySection>

            {/* Right Column - Payment Details */}
            <PaymentSection>
              <SectionTitle>Billing Information</SectionTitle>
              
              <FormGroup>
                <Label>Full Name *</Label>
                <Input
                  type="text"
                  value={billingInfo.name}
                  onChange={(e) => setBillingInfo({...billingInfo, name: e.target.value})}
                  placeholder="John Doe"
                />
              </FormGroup>

              <FormGroup>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={billingInfo.email}
                  onChange={(e) => setBillingInfo({...billingInfo, email: e.target.value})}
                  placeholder="john@example.com"
                />
              </FormGroup>

              <FormGroup>
                <Label>Phone</Label>
                <Input
                  type="tel"
                  value={billingInfo.phone}
                  onChange={(e) => setBillingInfo({...billingInfo, phone: e.target.value})}
                  placeholder="+1 234 567 8900"
                />
              </FormGroup>

              <Divider />

              <SectionTitle>Payment Method</SectionTitle>
              
              <PaymentMethods>
                <PaymentMethodCard
                  selected={paymentMethod === 'paypal'}
                  onClick={() => setPaymentMethod('paypal')}
                >
                  <input
                    type="radio"
                    checked={paymentMethod === 'paypal'}
                    onChange={() => setPaymentMethod('paypal')}
                  />
                  <div>
                    <strong>PayPal</strong>
                    <small>Secure payment via PayPal</small>
                  </div>
                </PaymentMethodCard>

                {/* Test payment option - Only visible to admin users */}
                {props.isAdmin && (
                  <PaymentMethodCard
                    selected={paymentMethod === 'test'}
                    onClick={() => setPaymentMethod('test')}
                  >
                    <input
                      type="radio"
                      checked={paymentMethod === 'test'}
                      onChange={() => setPaymentMethod('test')}
                    />
                    <div>
                      <strong>🔐 Test Payment (Admin Only)</strong>
                      <small>For development/testing only - Restricted to administrators</small>
                    </div>
                  </PaymentMethodCard>
                )}
              </PaymentMethods>

              {error && (
                <ErrorMessage>
                  <FiAlertCircle /> {error}
                </ErrorMessage>
              )}

              <PaymentActions>
                <SecondaryButton onClick={() => window.history.back()}>
                  Back to Cart
                </SecondaryButton>
                
                <PrimaryButton
                  onClick={paymentMethod === 'paypal' ? handlePayPalPayment : handleTestPayment}
                  disabled={processing || !billingInfo.name || !billingInfo.email}
                >
                  {processing ? (
                    <><FiLoader className="spin" /> Processing...</>
                  ) : (
                    <><FiCreditCard /> Pay ${formatPrice(finalTotal)}</>
                  )}
                </PrimaryButton>
              </PaymentActions>

              <SecurityNote>
                <FiCheck /> Secure checkout • Your information is protected
              </SecurityNote>
            </PaymentSection>
          </CheckoutGrid>
        </CheckoutContainer>
      </ContentContainer>
    </PageContainer>
  );
};

export default CheckoutPage;

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 100px 20px 40px;
`;

const CheckoutContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const CheckoutTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 32px 0;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: #3b82f6;
  }
`;

const CheckoutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 32px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const OrderSummarySection = styled.div``;

const PaymentSection = styled.div``;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 16px 0;
`;

const OrderItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const OrderItem = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
`;

const ItemImage = styled.img`
  width: 60px;
  height: 80px;
  object-fit: cover;
  border-radius: 4px;
`;

const ItemDetails = styled.div`
  flex: 1;
`;

const ItemName = styled.div`
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
`;

const ItemType = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const ItemQuantity = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const ItemPrice = styled.div`
  font-weight: 700;
  color: #1f2937;
  font-size: 16px;
`;

const PriceSummary = styled.div`
  border-top: 2px solid #e5e7eb;
  padding-top: 16px;
`;

const PriceRow = styled.div<{ discount?: boolean }>`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  color: ${props => props.discount ? '#10b981' : '#6b7280'};
  font-weight: ${props => props.discount ? '600' : '400'};
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 16px;
  border-top: 2px solid #e5e7eb;
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
`;

const SavingsText = styled.div`
  text-align: right;
  color: #10b981;
  font-size: 14px;
  margin-top: 8px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: #e5e7eb;
  margin: 24px 0;
`;

const PaymentMethods = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`;

const PaymentMethodCard = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 2px solid ${props => props.selected ? '#3b82f6' : '#e5e7eb'};
  border-radius: 8px;
  cursor: pointer;
  background: ${props => props.selected ? '#eff6ff' : 'white'};
  transition: all 0.2s;

  &:hover {
    border-color: #3b82f6;
  }

  input[type="radio"] {
    width: 20px;
    height: 20px;
    cursor: pointer;
  }

  strong {
    display: block;
    color: #1f2937;
    margin-bottom: 4px;
  }

  small {
    display: block;
    color: #6b7280;
    font-size: 12px;
  }
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background: #fee2e2;
  border: 1px solid #ef4444;
  border-radius: 8px;
  color: #991b1b;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const PaymentActions = styled.div`
  display: flex;
  gap: 12px;
`;

const SecondaryButton = styled.button`
  flex: 1;
  padding: 14px 24px;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e5e7eb;
  }
`;

const PrimaryButton = styled.button`
  flex: 2;
  padding: 14px 24px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #2563eb;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const SecurityNote = styled.div`
  text-align: center;
  font-size: 12px;
  color: #6b7280;
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  svg {
    color: #10b981;
  }
`;

const SuccessCard = styled.div`
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
  padding: 48px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #d1fae5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;

  svg {
    font-size: 40px;
    color: #10b981;
  }
`;

const SuccessTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
`;

const SuccessMessage = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 24px 0;
`;

const OrderNumber = styled.div`
  padding: 16px;
  background: #f3f4f6;
  border-radius: 8px;
  margin-bottom: 32px;
  color: #374151;

  strong {
    color: #1f2937;
    font-family: 'Courier New', monospace;
  }
`;

const SuccessActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;

  button {
    padding: 12px 24px;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:first-child {
      background: #f3f4f6;
      color: #374151;

      &:hover {
        background: #e5e7eb;
      }
    }

    &:last-child {
      background: #3b82f6;
      color: white;

      &:hover {
        background: #2563eb;
      }
    }
  }
`;

const EmptyCart = styled.div`
  max-width: 500px;
  margin: 0 auto;
  text-align: center;
  padding: 60px 20px;

  svg {
    color: #9ca3af;
    margin-bottom: 24px;
  }

  h2 {
    font-size: 24px;
    color: #1f2937;
    margin: 0 0 12px 0;
  }

  p {
    color: #6b7280;
    margin: 0 0 24px 0;
  }

  button {
    padding: 12px 32px;
    background: #3b82f6;
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      background: #2563eb;
    }
  }
`;

